import { Button } from './ui/button';

/**
 * Shared window selector for analytics pages (iter 132). Emits either:
 *   { window: 'today'|'yesterday'|'week'|'month'|'all' }
 *   { start: ISO, end: ISO }     — custom range
 *
 * Controlled via `value` and `onChange`. Presets are 5 quick buttons,
 * and a "Custom" mode reveals two date inputs. Kept stateless so both
 * analytics pages reuse the same component.
 */
export default function DateWindowPicker({ value, onChange, testidPrefix = 'window' }) {
  const preset = value?.window || null;
  const isCustom = !preset && !!(value?.start && value?.end);

  const setPreset = (w) => onChange({ window: w });
  const setCustom = (key, v) => {
    const s = key === 'start' ? v : (value?.start || '');
    const e = key === 'end' ? v : (value?.end || '');
    onChange({ start: s, end: e });
  };

  const presets = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'week', label: 'This Week' },
    { key: 'month', label: 'This Month' },
    { key: 'all', label: 'All Time' },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${testidPrefix}-picker`}>
      {presets.map(({ key, label }) => (
        <Button
          key={key}
          size="sm"
          variant={preset === key ? 'default' : 'outline'}
          onClick={() => setPreset(key)}
          className={preset === key ? 'bg-[#D4AF37] text-black hover:bg-[#F5D76E]' : 'border-white/10'}
          data-testid={`${testidPrefix}-preset-${key}`}
        >
          {label}
        </Button>
      ))}
      <Button
        size="sm"
        variant={isCustom ? 'default' : 'outline'}
        onClick={() => {
          const now = new Date().toISOString().slice(0, 10);
          const week = new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10);
          onChange({ start: week, end: now });
        }}
        className={isCustom ? 'bg-indigo-500 text-white' : 'border-white/10'}
        data-testid={`${testidPrefix}-preset-custom`}
      >
        Custom
      </Button>

      {isCustom && (
        <div className="flex items-center gap-2 pl-2 border-l border-white/10">
          <input
            type="date"
            value={value.start?.slice(0, 10) || ''}
            onChange={(e) => setCustom('start', e.target.value)}
            className="bg-[#0B0E14] border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
            data-testid={`${testidPrefix}-custom-start`}
          />
          <span className="text-xs text-gray-500">→</span>
          <input
            type="date"
            value={value.end?.slice(0, 10) || ''}
            onChange={(e) => setCustom('end', e.target.value)}
            className="bg-[#0B0E14] border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
            data-testid={`${testidPrefix}-custom-end`}
          />
        </div>
      )}
    </div>
  );
}
